import { useStore } from 'hooks/useStore';
import {
  allOptionsInterface,
  NDCFormArrayFieldInteface,
  NDCFormFields,
  NDCFormFieldsTypes,
} from 'Interfaces/FactsInterfaces/drugInterfaces';
import {
  ndcPpsPpsReasonUpdateRequestInterface,
  ndcSegmentationDpStrategyUpdateRequestInterface,
} from 'Interfaces/FactsInterfaces/ndcInterfaces';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { factScreenServices } from 'services/factsScreenServices';
import { DateConversionUtility, getFirstDayOfMonth, getLastDayOfMonth } from 'utils/dateUtilities';
import { deepCopy } from 'utils/deepCopy';
import { checkIfNDCStrategyAddEnabled } from 'utils/facts/roleBasedFunctionalityAccess';
import {
  convertNdcFormDataToAllOptions,
  createNdcPopUpDataForIndividualEdit,
  modifyNdcPopUpDateForIndividualEdit,
  selectedOptionUtility,
} from '../../utils/utilities';
import { handleCheckErrorForNdcIndividualEdit } from './utils/validateFormSubmit';

export const useNdcAddModal = () => {
  const {
    UserStore: { userModulesRoles },
    FactsStore: {
      selectedNdcRowData,
      ndcTablesData,
      isNcdTableLoading,
      handleFetchNdcHistory,
      isNdcHistoryLoading,
      ndcFormDropDownData,
      ndcHistoryData,
      handleUpdateNdcHistoryData,
      addModalVisibility,
      toggleAddModalVisibility,
    },
  } = useStore();
  const [ndcStrategyIndividualEditData, setNdcStrategyIndividualEditData] =
    useState<NDCFormFields | null>(
      selectedNdcRowData
        ? createNdcPopUpDataForIndividualEdit(
            modifyNdcPopUpDateForIndividualEdit(selectedNdcRowData),
            false,
            true
          )
        : null
    );
  const [initialNdcStrategyIndividualEditData, setInitialNdcStrategyIndividualEditData] =
    useState<NDCFormFields | null>(
      selectedNdcRowData
        ? createNdcPopUpDataForIndividualEdit(
            modifyNdcPopUpDateForIndividualEdit(selectedNdcRowData),
            false,
            true
          )
        : null
    );
  const [allOptions, setAllOptions] = useState<allOptionsInterface | null>({
    PPS: [],
    PPSSC: [],
    Segmentation: [],
    DPStrategy: [],
  });
  const [isNdcUpdating, setIsNdcUpdating] = useState(false);
  const [isHistoryVisible, setIsHistoryVisible] = useState(false);
  const [errorDetails, setErrorDetails] = useState({ isError: false, errorDetails: '' });
  const [addModalHeading, setAddModalHeading] = useState<NDCFormFieldsTypes | null>(null);
  const [newCreatedOption, _setNewCreatedOption] = useState<string>('');
  const [isNewNdcAddVisible, setIsNewNdcAddVisible] = useState(false);
  const [newlyAddedFutureNdcData, setNewlyAddedFutureNdcData] = useState<
    { heading: string; value: string; startDate: string; endDate: string }[]
  >([]);

  const allDropdownOptions = () => {
    const tempFormFields: { name: string; code: NDCFormFieldsTypes }[] = [];
    if (ndcStrategyIndividualEditData) {
      for (const [key, value] of Object.entries(ndcStrategyIndividualEditData)) {
        if (!value.selected) {
          if (key === 'PPSSC') {
            // PPSSC handled with PPS
          } else if (key === 'PPS')
            tempFormFields.push({ name: 'PPS Strategy & Reason', code: key as NDCFormFieldsTypes });
          else {
            tempFormFields.push({ name: value.fieldName, code: key as NDCFormFieldsTypes });
          }
        }
      }
    }
    return tempFormFields;
  };

  const checkIfDropdownOptionDisabled = (option: { code: string; name: string }) => {
    if (userModulesRoles.plm.length > 0) return false;
    return checkIfNDCStrategyAddEnabled(
      option.code as NDCFormFieldsTypes,
      userModulesRoles.facts,
      userModulesRoles
    )
      ? false
      : true;
  };

  const [allStrategyOptions, setAllStrategyOptions] = useState(allDropdownOptions());
  const resetError = () => {
    setErrorDetails({
      isError: false,
      errorDetails: '',
    });
  };

  const formUpdateMethods = {
    handleUpdateValue: (fieldName: NDCFormFieldsTypes, value: string) => {
      resetError();
      if (ndcStrategyIndividualEditData) {
        const tempNdcEditData = deepCopy(ndcStrategyIndividualEditData);
        tempNdcEditData[fieldName].value = value;
        setNdcStrategyIndividualEditData(tempNdcEditData);
      }
    },
    handleUpdateStartDate: (fieldName: NDCFormFieldsTypes, newStartDate: Date) => {
      resetError();
      if (ndcStrategyIndividualEditData) {
        const tempNdcEditData = deepCopy(ndcStrategyIndividualEditData);
        tempNdcEditData[fieldName].startDate =
          DateConversionUtility.convertLocalToDBDateString(newStartDate);
        if (fieldName === 'PPS') {
          tempNdcEditData['PPSSC'].startDate =
            DateConversionUtility.convertLocalToDBDateString(newStartDate);
        }
        if (fieldName === 'DPStrategy' || fieldName === 'Segmentation') {
          tempNdcEditData[fieldName].startDate = DateConversionUtility.convertLocalToDBDateString(
            getFirstDayOfMonth(newStartDate.toDateString())
          );
        }
        setNdcStrategyIndividualEditData(tempNdcEditData);
      }
    },
    handleUpdateEndDate: (fieldName: NDCFormFieldsTypes, newStartDate: Date) => {
      resetError();

      if (ndcStrategyIndividualEditData) {
        const tempNdcEditData = deepCopy(ndcStrategyIndividualEditData);
        tempNdcEditData[fieldName].endDate =
          DateConversionUtility.convertLocalToDBDateString(newStartDate);
        if (fieldName === 'PPS') {
          tempNdcEditData['PPSSC'].endDate =
            DateConversionUtility.convertLocalToDBDateString(newStartDate);
        }
        if (fieldName === 'DPStrategy' || fieldName === 'Segmentation') {
          tempNdcEditData[fieldName].endDate = DateConversionUtility.convertLocalToDBDateString(
            getLastDayOfMonth(newStartDate.toDateString())
          );
        }
        setNdcStrategyIndividualEditData(tempNdcEditData);
      }
    },
    handleCheckIfPpsPpsscShouldUPdate: () => {
      if (!ndcStrategyIndividualEditData?.PPS.selected) return false;
      if (
        ndcStrategyIndividualEditData?.PPS.value !=
          initialNdcStrategyIndividualEditData?.PPS.value ||
        ndcStrategyIndividualEditData?.PPSSC.value !=
          initialNdcStrategyIndividualEditData?.PPSSC.value ||
        ndcStrategyIndividualEditData?.PPS.startDate !==
          initialNdcStrategyIndividualEditData?.PPS.startDate ||
        ndcStrategyIndividualEditData?.PPS.endDate !==
          initialNdcStrategyIndividualEditData?.PPS.endDate
      ) {
        return true;
      }
      return false;
    },

    handleCheckIfDpStrategyShouldUPdate: () => {
      if (!ndcStrategyIndividualEditData?.DPStrategy.selected) return false;
      if (
        ndcStrategyIndividualEditData?.DPStrategy.value !=
          initialNdcStrategyIndividualEditData?.DPStrategy.value ||
        ndcStrategyIndividualEditData?.DPStrategy.startDate !==
          initialNdcStrategyIndividualEditData?.DPStrategy.startDate ||
        ndcStrategyIndividualEditData?.DPStrategy.endDate !==
          initialNdcStrategyIndividualEditData?.DPStrategy.endDate
      ) {
        return true;
      }
      return false;
    },

    handleCheckIfSegmentationShouldUPdate: () => {
      if (!ndcStrategyIndividualEditData?.Segmentation.selected) return false;
      if (
        ndcStrategyIndividualEditData?.Segmentation.value !=
          initialNdcStrategyIndividualEditData?.Segmentation.value ||
        ndcStrategyIndividualEditData?.Segmentation.startDate !==
          initialNdcStrategyIndividualEditData?.Segmentation.startDate ||
        ndcStrategyIndividualEditData?.Segmentation.endDate !==
          initialNdcStrategyIndividualEditData?.Segmentation.endDate
      ) {
        return true;
      }
      return false;
    },

    handleUpdateButtonClick: async () => {
      resetError();

      if (!checkIfFormValuesAreChanged()) {
        return setErrorDetails({
          isError: true,
          errorDetails: 'Please change some value to update',
        });
      }

      if (
        selectedNdcRowData &&
        ndcStrategyIndividualEditData &&
        initialNdcStrategyIndividualEditData
      ) {
        if (
          ndcHistoryData &&
          handleCheckErrorForNdcIndividualEdit({
            ndcStrategyIndividualEditData,
            initialNdcStrategyIndividualEditData,
            setErrorDetails,
            futureNdcData: ndcHistoryData,
          })
        ) {
          return;
        }
        let tempPricingStrategyId = 0;
        ndcFormDropDownData?.PPSDescriptions.forEach(option => {
          if (option.PricingStrategyDescription === ndcStrategyIndividualEditData?.PPS.value) {
            tempPricingStrategyId = option.PricingStrategyId;
          }
        });
        let tempPricingStrategyReasonId = 0;
        ndcFormDropDownData?.PPSReasons.forEach(option => {
          if (option.Reason === ndcStrategyIndividualEditData?.PPSSC.value) {
            tempPricingStrategyReasonId = option.ReasonID;
          }
        });

        let tempReportingSegmentId = 0;
        ndcFormDropDownData?.ReportingSegments.forEach(option => {
          if (option.ReportingSegmentName === ndcStrategyIndividualEditData?.Segmentation.value) {
            tempReportingSegmentId = option.ReportingSegmentID;
          }
        });

        let tempReportingDPStrategyId = 0;
        ndcFormDropDownData?.DpStrategies.forEach(option => {
          if (option.ReportingSegmentName === ndcStrategyIndividualEditData?.DPStrategy.value) {
            tempReportingDPStrategyId = option.ReportingSegmentID;
          }
        });

        const tempNewNdcModalData: {
          heading: string;
          value: string;
          startDate: string;
          endDate: string;
        }[] = [];

        const paydloadPpsPpsReason: ndcPpsPpsReasonUpdateRequestInterface[] = [];
        if (formUpdateMethods.handleCheckIfPpsPpsscShouldUPdate()) {
          paydloadPpsPpsReason.push({
            pk: selectedNdcRowData?.PricingStrategyComponentID,
            NDC11: selectedNdcRowData.NDC11,
            PricingStrategyId: tempPricingStrategyId,
            PricingStrategyReasonId: tempPricingStrategyReasonId,
            StartDate: ndcStrategyIndividualEditData?.PPS.startDate || '',
            EndDate: ndcStrategyIndividualEditData?.PPS.endDate || '',
          });
        }

        if (formUpdateMethods.handleCheckIfPpsPpsscShouldUPdate()) {
          tempNewNdcModalData.push({
            heading: 'PPS',
            value: ndcStrategyIndividualEditData.PPS.value,
            startDate: ndcStrategyIndividualEditData?.PPS.startDate || '',
            endDate: ndcStrategyIndividualEditData?.PPS.endDate || '',
          });
          tempNewNdcModalData.push({
            heading: 'PPS Reason',
            value: ndcStrategyIndividualEditData.PPSSC.value,
            startDate: ndcStrategyIndividualEditData?.PPS.startDate || '',
            endDate: ndcStrategyIndividualEditData?.PPS.endDate || '',
          });
        }

        const payloadSegmentation: ndcSegmentationDpStrategyUpdateRequestInterface[] = [];

        if (
          ndcStrategyIndividualEditData?.Segmentation.value !==
            initialNdcStrategyIndividualEditData?.Segmentation.value ||
          ndcStrategyIndividualEditData?.Segmentation.startDate !==
            initialNdcStrategyIndividualEditData?.Segmentation.startDate ||
          ndcStrategyIndividualEditData?.Segmentation.endDate !==
            initialNdcStrategyIndividualEditData?.Segmentation.endDate
        ) {
          if (formUpdateMethods.handleCheckIfSegmentationShouldUPdate())
            payloadSegmentation.push({
              pk: selectedNdcRowData?.SegmentationID,
              NDC11: selectedNdcRowData.NDC11,
              ReportingSegmentID: tempReportingSegmentId,
              StartDate: ndcStrategyIndividualEditData?.Segmentation.startDate || '',
              EndDate: ndcStrategyIndividualEditData?.Segmentation.endDate || '',
            });
          tempNewNdcModalData.push({
            heading: 'Financial Segmentation',
            value: ndcStrategyIndividualEditData.Segmentation.value,
            startDate: ndcStrategyIndividualEditData?.Segmentation.startDate || '',
            endDate: ndcStrategyIndividualEditData?.Segmentation.endDate || '',
          });
        }

        if (
          ndcStrategyIndividualEditData?.DPStrategy.value !==
            initialNdcStrategyIndividualEditData?.DPStrategy.value ||
          ndcStrategyIndividualEditData?.DPStrategy.startDate !==
            initialNdcStrategyIndividualEditData?.DPStrategy.startDate ||
          ndcStrategyIndividualEditData?.DPStrategy.endDate !==
            initialNdcStrategyIndividualEditData?.DPStrategy.endDate
        ) {
          if (formUpdateMethods.handleCheckIfDpStrategyShouldUPdate())
            payloadSegmentation.push({
              pk: selectedNdcRowData?.DPstratergyID,
              NDC11: selectedNdcRowData.NDC11,
              ReportingSegmentID: tempReportingDPStrategyId,
              StartDate: ndcStrategyIndividualEditData?.DPStrategy.startDate || '',
              EndDate: ndcStrategyIndividualEditData?.DPStrategy.endDate || '',
            });
          tempNewNdcModalData.push({
            heading: 'DP Strategy',
            value: ndcStrategyIndividualEditData.DPStrategy.value,
            startDate: ndcStrategyIndividualEditData?.DPStrategy.startDate || '',
            endDate: ndcStrategyIndividualEditData?.DPStrategy.endDate || '',
          });
        }

        try {
          setErrorDetails({ isError: false, errorDetails: '' });
          setIsNdcUpdating(true);

          if (
            formUpdateMethods.handleCheckIfPpsPpsscShouldUPdate() ||
            formUpdateMethods.handleCheckIfDpStrategyShouldUPdate() ||
            formUpdateMethods.handleCheckIfSegmentationShouldUPdate()
          ) {
            const res = await factScreenServices.updateNdcPpsPpscInfo({
              ppsPpsReason: paydloadPpsPpsReason,
              segmentationDpStrategy: payloadSegmentation,
            });
            if (ndcHistoryData && ndcHistoryData.PPS_History) {
              const tempNdcHistoryData = deepCopy(ndcHistoryData);
              tempNdcHistoryData.PPS_History.future = res[0].ndc_future_values.PPS_History;
              tempNdcHistoryData.PPS_Reason_History.future =
                res[0].ndc_future_values.PPS_Reason_History;
              tempNdcHistoryData.Segmentation.future = res[0].ndc_future_values.Segmentation;
              tempNdcHistoryData.dp_strategy.future = res[0].ndc_future_values.dp_strategy;
              handleUpdateNdcHistoryData(tempNdcHistoryData);
            }
            const tempNdcInvidualData = deepCopy(ndcStrategyIndividualEditData);
            setInitialNdcStrategyIndividualEditData(tempNdcInvidualData);

            // set is new add visible to true for making the new add ndc visible
            setNewlyAddedFutureNdcData(tempNewNdcModalData);
            setIsNewNdcAddVisible(true);
          }
        } catch (err) {
          console.log(err);
          toast.error('Some error occurred while adding ndc details please try again, later');
        } finally {
          setIsNdcUpdating(false);
        }
      }
    },
  };

  const maxDateFromGivenAndTodays = (givenDate: Date) => {
    const date = new Date();
    if (givenDate > date) {
      return givenDate;
    } else {
      return date;
    }
  };

  const formFieldsArrayConverter = () => {
    const tempFormFields: NDCFormArrayFieldInteface[] = [];
    if (ndcStrategyIndividualEditData) {
      for (const [key, value] of Object.entries(ndcStrategyIndividualEditData)) {
        tempFormFields.push({ keyName: key as NDCFormFieldsTypes, ...value });
      }
    }
    return tempFormFields;
  };

  const selectedOption = (type: NDCFormFieldsTypes, value: string) => {
    if (ndcFormDropDownData)
      return selectedOptionUtility(
        convertNdcFormDataToAllOptions(ndcFormDropDownData),
        type,
        value
      );
  };

  const checkIfFormValuesAreChanged = () => {
    if (
      JSON.stringify(initialNdcStrategyIndividualEditData) ===
      JSON.stringify(ndcStrategyIndividualEditData)
    ) {
      return false;
    }
    return true;
  };

  useEffect(() => {
    if (ndcFormDropDownData) {
      setAllOptions(convertNdcFormDataToAllOptions(ndcFormDropDownData));
    }
  }, [ndcFormDropDownData, newCreatedOption]);

  useEffect(() => {
    if (selectedNdcRowData) {
      setNdcStrategyIndividualEditData(
        createNdcPopUpDataForIndividualEdit(
          modifyNdcPopUpDateForIndividualEdit(selectedNdcRowData),
          false,
          true
        )
      );
      setInitialNdcStrategyIndividualEditData(
        createNdcPopUpDataForIndividualEdit(
          modifyNdcPopUpDateForIndividualEdit(selectedNdcRowData),
          false,
          true
        )
      );
    }
  }, [selectedNdcRowData, ndcTablesData]);

  useEffect(() => {
    handleFetchNdcHistory();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load only
  }, []);

  const toggleHistoryVisibility = () => {
    setIsHistoryVisible(!isHistoryVisible);
  };

  const handleNewStrategySelect = (
    newOption: { code: string; name: string },
    fieldName: NDCFormFieldsTypes
  ) => {
    if (ndcFormDropDownData && ndcStrategyIndividualEditData) {
      const tempOptions = convertNdcFormDataToAllOptions(ndcFormDropDownData);
      setAllOptions(tempOptions);
      formUpdateMethods.handleUpdateValue(fieldName, newOption.code);
    }
  };

  const toggleNewNdcPopVisibility = () => {
    setIsNewNdcAddVisible(!isNewNdcAddVisible);
    setNewlyAddedFutureNdcData([]);
  };

  const getSelectedOption = (value: string, fieldName: NDCFormFieldsTypes) => {
    let result = '';
    if (allOptions) {
      allOptions[fieldName].forEach(option => {
        if (option.code === value) result = option.code;
      });
    }
    return result;
  };

  const isAnyStrategySelected = () => {
    let flag = false;
    if (ndcStrategyIndividualEditData) {
      for (const [_key, value] of Object.entries(ndcStrategyIndividualEditData)) {
        if (value.selected) flag = true;
      }
    }
    return flag;
  };

  const handleStrategySelect = (strategyName: NDCFormFieldsTypes) => {
    if (ndcStrategyIndividualEditData && initialNdcStrategyIndividualEditData) {
      const tempNDCData = deepCopy(ndcStrategyIndividualEditData);
      tempNDCData[strategyName].selected = true;
      if (strategyName === 'PPS' || strategyName === 'PPSSC') {
        tempNDCData['PPS'].selected = true;
        tempNDCData['PPSSC'].selected = true;
      }
      setNdcStrategyIndividualEditData(tempNDCData);

      const tempInitialNDCData = deepCopy(initialNdcStrategyIndividualEditData);
      tempInitialNDCData[strategyName].selected = true;
      if (strategyName === 'PPS' || strategyName === 'PPSSC') {
        tempInitialNDCData['PPS'].selected = true;
        tempInitialNDCData['PPSSC'].selected = true;
      }
      setInitialNdcStrategyIndividualEditData(tempInitialNDCData);

      const tempFormFields: { name: string; code: NDCFormFieldsTypes }[] = [];
      for (const [key, value] of Object.entries(ndcStrategyIndividualEditData)) {
        if (!value.selected)
          tempFormFields.push({ name: value.fieldName, code: key as NDCFormFieldsTypes });
      }
      setAllStrategyOptions(tempFormFields);
    }
  };

  const handleUnselectStrategy = (strategyName: NDCFormFieldsTypes) => {
    resetError();

    if (ndcStrategyIndividualEditData && initialNdcStrategyIndividualEditData) {
      const tempNDCData = deepCopy(ndcStrategyIndividualEditData);
      tempNDCData[strategyName].selected = false;
      if (strategyName === 'PPS' || strategyName === 'PPSSC') {
        tempNDCData['PPS'].selected = false;
        tempNDCData['PPSSC'].selected = false;
      }
      setNdcStrategyIndividualEditData(tempNDCData);

      const tempInitialNDCData = deepCopy(initialNdcStrategyIndividualEditData);
      tempInitialNDCData[strategyName].selected = false;
      if (strategyName === 'PPS' || strategyName === 'PPSSC') {
        tempInitialNDCData['PPS'].selected = false;
        tempInitialNDCData['PPSSC'].selected = false;
      }
      setInitialNdcStrategyIndividualEditData(tempInitialNDCData);

      const tempFormFields: { name: string; code: NDCFormFieldsTypes }[] = [];
      for (const [key, value] of Object.entries(ndcStrategyIndividualEditData)) {
        if (!value.selected)
          tempFormFields.push({ name: value.fieldName, code: key as NDCFormFieldsTypes });
      }
      setAllStrategyOptions(tempFormFields);
    }
  };

  return {
    checkIfDropdownOptionDisabled,
    userModulesRoles,
    isNdcUpdating,
    isNcdTableLoading,
    allDropdownOptions,
    isAnyStrategySelected,
    handleStrategySelect,
    handleUnselectStrategy,
    selectedNdcRowData,
    errorDetails,
    ndcStrategyIndividualEditData,
    isNdcHistoryLoading,
    convertNdcFormDataToAllOptions,
    initialNdcStrategyIndividualEditData,
    maxDateFromGivenAndTodays,
    formUpdateMethods,
    isHistoryVisible,
    toggleHistoryVisibility,
    formFieldsArrayConverter,
    selectedOption,
    allOptions,
    addModalVisibility,
    toggleAddModalVisibility,
    handleNewStrategySelect,
    addModalHeading,
    setAddModalHeading,
    ndcFormDropDownData,
    getSelectedOption,
    isNewNdcAddVisible,
    newlyAddedFutureNdcData,
    toggleNewNdcPopVisibility,
    allStrategyOptions,
  };
};
